import axios from 'axios';
import WOOCOMMERCE_URL from './client';
import { getNonce, setNonce, clearNonce } from './nonceStorage';

// ============================================================
// Ta warstwa rozmawia z mu-pluginem "React SSO Auth Bridge"
// (wordpress-mu-plugin/react-sso-auth.php — musi być wgrany do
// wp-content/mu-plugins na serwerze WordPress), NIE z CoCart/JWT.
//
// Logowanie przez JWT nie ustawia ciasteczka sesji WP, więc klasyczna
// strona kasy WooCommerce (i np. /moje-konto/) nigdy nie widziałaby
// użytkownika jako zalogowanego. Mu-plugin ustawia PRAWDZIWE ciasteczko
// sesji (wp_set_auth_cookie), dzięki czemu React, CoCart i klasyczna kasa
// zgadzają się co do tego, kto jest zalogowany.
// ============================================================

const SSO_BASE_URL = `${WOOCOMMERCE_URL.replace(/\/$/, '')}/wp-json/react-sso/v1`;

// Ciasteczka sesji muszą być wysyłane, żeby WordPress w ogóle wiedział,
// kto pyta — stąd withCredentials: true tylko dla tego klienta.
const ssoClient = axios.create({
    baseURL: SSO_BASE_URL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

export interface CurrentUser {
    id: number;
    name: string;
    email?: string;
}

interface SsoUserPayload {
    id: number;
    name: string;
    email?: string;
    nonce: string;
}

const applyUserPayload = (payload: SsoUserPayload): CurrentUser => {
    setNonce(payload.nonce);
    return { id: payload.id, name: payload.name, email: payload.email };
};

// ------------------------------------------------------------
// Logowanie — ustawia prawdziwe ciasteczko sesji WP po stronie serwera.
// ------------------------------------------------------------
export const login = async (
    username: string,
    password: string,
): Promise<{ success: boolean; user: CurrentUser | null; message?: string }> => {
    try {
        const response = await ssoClient.post('/login', { username, password });

        if (!response.data?.success || !response.data?.user) {
            return { success: false, user: null };
        }

        return { success: true, user: applyUserPayload(response.data.user) };
    } catch (error: any) {
        return {
            success: false,
            user: null,
            message: error?.response?.data?.message || 'Nieprawidłowy login lub hasło',
        };
    }
};

// ------------------------------------------------------------
// Wylogowanie — kasuje ciasteczko sesji WP po stronie serwera.
// ------------------------------------------------------------
export const logoutRequest = async (): Promise<void> => {
    try {
        await ssoClient.post('/logout');
    } catch (error) {
        console.warn('⚠️ Wylogowanie po stronie serwera nie powiodło się:', error);
    } finally {
        clearNonce();
    }
};

// ------------------------------------------------------------
// Sprawdzenie stanu logowania — działa niezależnie od tego, czy
// zalogowano się przez React, czy bezpośrednio w sklepie, bo sprawdza
// ciasteczko sesji, a nie nasz lokalny stan.
// ------------------------------------------------------------
export const checkStatus = async (): Promise<CurrentUser | null> => {
    try {
        const response = await ssoClient.get('/status');

        if (!response.data?.logged_in || !response.data?.user) {
            clearNonce();
            return null;
        }

        return applyUserPayload(response.data.user);
    } catch (error) {
        console.warn('⚠️ Nie udało się sprawdzić stanu logowania:', error);
        return null;
    }
};

export { getNonce, clearNonce };
